import { MediaType } from '../../helpers/file-system/file.helper';
import { MediaPickerFiltersModel } from './media-picker-filters.model';
import { MediaPickerItemModel } from './media-picker-item.model';
import { MediaPickerPageInfoModel } from './media-picker-page-info.model';

/**
 * Filters an item using the filters state
 * @param item {MediaPickerItemModel}
 * @param filters {MediaPickerFiltersModel}
 * @return true if the item matches the filters, false otherwise
 */
export const filterItem = (
    item: MediaPickerItemModel,
    filters: MediaPickerFiltersModel
): boolean => {

    if (filters.lastModifiedDate !== 0 &&
        item.lastModifiedDate < Date.now() - filters.lastModifiedDate
    ) {
        return false;
    }

    switch (item.mediaType) {
        case MediaType.audio: {
            return filters.audio;
        }
        case MediaType.image: {
            return filters.image;
        }
        case MediaType.other: {
            return filters.other;
        }
        case MediaType.pdf: {
            return filters.pdf;
        }
        case MediaType.video: {
            return filters.video;
        }
        default: {
            return true;
        }
    }
};
/**
 * Filters for invalid files names
 * @param name {string} the file name
 * @return false if the name is invalid, true otherwise.
 */
export const fileNameFilter = (name: string): boolean => {
    return !(
        name.startsWith('.') ||
        name.endsWith('.cache') ||
        name.endsWith('.db-wal')
    );
};

/**
 * Calculates the list of item ids that should be displayed in the view, taking
 * into account the state of the filters and the page info. The reason this
 * function is not part of a selector is that we need to save this value into
 * the state. The reason for saving into the state is that when we calculate a
 * range of ids for selecting (via touch events), the range should only include
 * items that are visible, thus if the visible ids are calculated in a selector
 * we would need to recalculate the visible id list each time the user selected
 * a new item.
 * @param items {Array<MediaPickerItemModel>}
 * @param filters {MediaPickerFiltersModel}
 * @param pageInfo {MediaPickerPageInfoModel}
 * @return {Array<string>}
 */
export const getVisibleItemIds = (
    items: Array<MediaPickerItemModel>,
    filters: MediaPickerFiltersModel,
    pageInfo: MediaPickerPageInfoModel
): Array<string> => {
    const newItems = [];
    const limit = pageInfo.page * pageInfo.pageSize + 1;
    let i = 0;
    let item;
    while (i < limit) {
        item = items[i];
        i++;
        if (item === undefined) {
            break;
        }
        if (!filterItem(item, filters)) {
            continue;
        }
        newItems.push(item.id);
    }
    return newItems;
};

/**
 * Returns a list of ids that are between (inclusive) the from and to ids. If
 * either the from or to ids are not found, an empty array is returned. We use
 * this to calculate the list of ids that should be selected when the user drags
 * their finger across the grid.
 * @param from {string}
 * @param to {string}
 * @param ids {Array<string>}
 * @return {Array<string>}
 */
export const getIdRange = (
    from: string,
    to: string,
    ids: Array<string>
): Array<string> => {

    const [i0, i1] = [
        ids.indexOf(from),
        ids.indexOf(to)
    ].sort((a, b) => a - b);

    const idsOut = [];

    if (i0 === -1 || i1 === -1) {
        return [];
    }

    for (let i = i0; i <= i1; i++) {
        idsOut.push(ids[i]);
    }
    return idsOut;
};



// WEBPACK FOOTER //
// ./src/app/modals-popovers/media-picker-modal/helper.ts